import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";

// Fallback list used only if the admin-managed list in the DB is empty.
export const HOME_VIDEOS = [
  { type: "youtube", id: "agbYag1jD2I", title: "AI Shorthand Short — New" },
  { type: "youtube", id: "ArK0E4ClZmM", title: "AI Shorthand — Latest Short" },
  { type: "youtube", id: "JD8d4NCK2Io", title: "AI Shorthand Short — Featured" },
  { type: "mp4", src: "/videos/explainer.mp4", title: "See AI Shorthand in action" },
  { type: "youtube", id: "aWExI54t_EA", title: "AI Shorthand Short #1" },
  { type: "youtube", id: "3pkD4WyXAYg", title: "AI Shorthand Short #2" },
  { type: "youtube", id: "Wu7uPxBc9JA", title: "AI Shorthand Short #3" },
  { type: "youtube", id: "QE-xD-hNrjw", title: "AI Shorthand Short #4" },
  { type: "youtube", id: "SPbfxONpthA", title: "AI Shorthand Short #6" },
];

const extractYouTubeId = (raw) => {
  if (!raw) return "";
  if (/^[\w-]{11}$/.test(raw)) return raw;
  const m = raw.match(/(?:v=|\/shorts\/|youtu\.be\/|\/embed\/)([\w-]{11})/);
  return m ? m[1] : raw;
};

export const VideoCarousel = ({ videos, page = "home" }) => {
  const scrollerRef = useRef(null);
  const [list, setList] = useState(videos || null);

  useEffect(() => {
    if (videos && videos.length) return; // caller supplied a list
    let active = true;
    api.get("/home-videos", { params: { page } }).then(({ data }) => {
      if (!active) return;
      const mapped = (data || []).map((v) => ({
        type: v.type,
        id: v.video_id,
        src: v.src,
        title: v.title,
      }));
      // Fallback to the built-in list only for the home page
      setList(mapped.length ? mapped : (page === "home" ? HOME_VIDEOS : []));
    }).catch(() => setList(page === "home" ? HOME_VIDEOS : []));
    return () => { active = false; };
  }, [videos, page]);

  const effective = list || (page === "home" ? HOME_VIDEOS : []);

  if (!effective.length) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-item]");
    const step = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const hasMultiple = effective.length > 1;

  return (
    <div className="relative" data-testid="home-video-carousel">
      {hasMultiple && (
        <>
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Previous video"
            data-testid="carousel-prev"
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-11 h-11 items-center justify-center rounded-full bg-white border-2 border-[#D4AF37]/60 shadow-lg hover:bg-[#D4AF37]/10 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-[#0B0F0D]" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Next video"
            data-testid="carousel-next"
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-11 h-11 items-center justify-center rounded-full bg-white border-2 border-[#D4AF37]/60 shadow-lg hover:bg-[#D4AF37]/10 transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-[#0B0F0D]" />
          </button>
        </>
      )}

      <div
        ref={scrollerRef}
        className={`flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 px-2 sm:px-6 -mx-2 sm:-mx-6 ${hasMultiple ? "" : "justify-center"}`}
        style={{ scrollbarWidth: "thin" }}
      >
        {effective.map((v, idx) => (
          <div
            key={idx}
            data-carousel-item
            data-testid={`carousel-item-${idx}`}
            className="snap-center shrink-0 w-[280px] sm:w-[300px] rounded-[2rem] overflow-hidden border-2 border-[#D4AF37]/50 shadow-xl bg-black"
          >
            <div className="relative w-full aspect-[9/16] bg-black">
              {v.type === "mp4" ? (
                <video
                  data-testid={`carousel-video-${idx}`}
                  src={v.src}
                  controls
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <iframe
                  data-testid={`carousel-youtube-${idx}`}
                  src={`https://www.youtube.com/embed/${extractYouTubeId(v.id)}?rel=0&modestbranding=1`}
                  title={v.title || `Video ${idx + 1}`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              )}
            </div>
            {v.title && (
              <div className="px-4 py-3 bg-black text-white text-sm font-semibold text-center">
                {v.title}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VideoCarousel;
